import { ButtonPrimary, ButtonSecondary, IconButton } from "@nova/component-btn"
import { TriCheckbox } from "@nova/component-checkbox"
import { Input, InputOutlined } from "@nova/ds-component-input"
import * as Icons from "@nova/component-icons"
import { Dropdown } from "@nova/component-dropdown"
import { Select, type SelectItem } from "@nova/component-select"
import {
    CreatableReportTemplate,
    ReportDateRange,
    ReportFilters,
    ReportFormatConfig,
    ReportSchedule,
    ReportSortField,
    creatableReportTemplateSchema,
} from "@nova/schemas"
import { Card } from "features/pilot-tracking/MetricLayout"
import { usePilots } from "features/pilot-tracking/pilots/PilotQueries"
import { useAircraft } from "features/pilot-tracking/aircraft/AircraftQueries"
import { Divider } from "components/Divider"
import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from "react"
import classNames from "classnames"
import { ToggleSwitch } from "@nova/component-toggle-switch"
import { RenamableText } from "features/data-management/workspace/TODO-put-in-package/RenamableText"
import { ClickAwayListenerV2 } from "@nova/component-click-away-listener"
import { createQuery } from "@nova/queryable"
import { usePilotTrackingQuery } from "../PilotTrackingQueries"
import { ExpandableContent } from "@nova/component-expandable-content"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { possibleTimezones } from "@nova/constants"
import {
    selectTimezoneId,
    setTimezoneId,
} from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/slice"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { PortalledTooltip } from "features/popups/PortalledTooltip"
import {
    useConfirmationModalContext,
    getGenericConfirmationModalComponent,
} from "features/modals/WorkspaceConfirmationModalV2"
import { useBlocker } from "react-router"
import { ContactInputMultiSelect } from "themes/default-tailwind/buttons/ContactInput"

interface ReportTemplateFormState {
    name: string
    description: string | null
    reportType: CreatableReportTemplate["report_type"]
    dateRangeType: Exclude<ReportDateRange["type"], "last_calendar_month">
    relativeDays: number | "last_calendar_month"
    monthYear: number | "current"
    monthMonth: number | "current"
    yearToDateYear: number | "current"
    customStart: string
    customEnd: string
    isActive: boolean
    recipients: string[]
    frequency: ReportSchedule["frequency"]
    sendTime: string
    startDate: string
    timezone: string | null
    saveTimezoneAsDefault: boolean
}

type ReportColumns = NonNullable<ReportFormatConfig["columns"]>
type ReportUnits = NonNullable<ReportFormatConfig["units"]>
type ReportSort = NonNullable<ReportFormatConfig["sort"]>

type FieldSetter = <K extends keyof ReportTemplateFormState>(
    key: K,
    value: ReportTemplateFormState[K]
) => void

type FilterSetter = <K extends keyof ReportFilters>(key: K, value: ReportFilters[K]) => void

const CUSTOM_FIELDS_KEY = "custom_fields"

const REPORT_COLUMNS = {
    flight: [
        { key: "id", label: "ID" },
        { key: "location", label: "Location" },
        { key: "takeoff_latitude", label: "Takeoff Latitude" },
        { key: "takeoff_longitude", label: "Takeoff Longitude" },
        { key: "pilot_in_command", label: "Pilot in Command" },
        { key: "duration", label: "Duration" },
        { key: "checklist_status", label: "Checklist Status" },
        { key: "num_images", label: "Number of Images" },
        { key: "num_videos", label: "Number of Videos" },
        { key: "notes", label: "Notes" },
        { key: CUSTOM_FIELDS_KEY, label: "Custom fields" },
    ],
    drones: [
        { key: "nickname", label: "Nickname" },
        { key: "manufacturer", label: "Manufacturer" },
        { key: "model", label: "Model" },
        { key: "serial_number", label: "Serial Number" },
        { key: "registration_number", label: "Registration Number" },
        { key: "firmware_version", label: "Firmware Version" },
    ],
    metrics: [
        { key: "avg_altitude", label: "Average Altitude" },
        { key: "max_altitude", label: "Max Altitude" },
        { key: "avg_speed", label: "Average Speed" },
        { key: "max_speed", label: "Max Speed" },
    ],
} as const

type ReportColumnGroup = keyof typeof REPORT_COLUMNS

const REPORT_COLUMN_GROUPS = Object.keys(REPORT_COLUMNS) as ReportColumnGroup[]

const groupKeys = (group: ReportColumnGroup) =>
    REPORT_COLUMNS[group].map((column) => column.key as string)

const groupHasCustomFields = (group: ReportColumnGroup) =>
    groupKeys(group).includes(CUSTOM_FIELDS_KEY)

const selectedKeys = (format: ReportColumns, group: ReportColumnGroup) => {
    const keys: string[] = [...(format[group] ?? [])]
    if (groupHasCustomFields(group) && format.customFields) keys.push(CUSTOM_FIELDS_KEY)
    return keys
}

const withSelectedKeys = (format: ReportColumns, group: ReportColumnGroup, keys: string[]) => {
    const next = {
        ...format,
        [group]: keys.filter((key) => key !== CUSTOM_FIELDS_KEY),
    } as ReportColumns
    if (groupHasCustomFields(group)) next.customFields = keys.includes(CUSTOM_FIELDS_KEY)
    return next
}

const DEFAULT_COLUMNS: Record<CreatableReportTemplate["report_type"], ReportColumns> = {
    flight_report: {
        flight: [
            "id",
            "location",
            "takeoff_latitude",
            "takeoff_longitude",
            "pilot_in_command",
            "duration",
        ],
        drones: ["nickname", "manufacturer", "model", "serial_number", "registration_number"],
        metrics: ["max_altitude"],
    },
    coa_report: {
        flight: [
            "id",
            "pilot_in_command",
            "location",
            "takeoff_latitude",
            "takeoff_longitude",
            "checklist_status",
        ],
        drones: [],
        metrics: ["max_altitude"],
    },
}

const UNITS_OPTIONS: {
    DATE: { label: string; value: ReportUnits["date"] }[]
    TIME: { label: string; value: ReportUnits["time"] }[]
    DISTANCE: { label: string; value: ReportUnits["distance"] }[]
    ALTITUDE: { label: string; value: ReportUnits["altitude"] }[]
    SPEED: { label: string; value: ReportUnits["speed"] }[]
    TEMPERATURE: { label: string; value: ReportUnits["temperature"] }[]
} = {
    DATE: [
        { label: "MM/DD/YYYY", value: "MM/DD/YYYY" },
        { label: "DD/MM/YYYY", value: "DD/MM/YYYY" },
    ],
    TIME: [
        { label: "12 hours", value: "12h" },
        { label: "24 hours", value: "24h" },
    ],
    DISTANCE: [
        { label: "Feet", value: "feet" },
        { label: "Miles", value: "miles" },
        { label: "Meters", value: "meters" },
        { label: "Kilometers", value: "kilometers" },
    ],
    ALTITUDE: [
        { label: "Feet", value: "feet" },
        { label: "Meters", value: "meters" },
    ],
    SPEED: [
        { label: "Miles per hour", value: "mph" },
        { label: "Kilometers per hour", value: "kph" },
    ],
    TEMPERATURE: [
        { label: "Fahrenheit", value: "fahrenheit" },
        { label: "Celsius", value: "celsius" },
    ],
}

const SORT_FIELD_OPTIONS: { label: string; value: ReportSortField }[] = [
    { label: "Flight Date Time", value: "started_at" },
    { label: "Pilot in Command", value: "pilot_in_command" },
    { label: "Flight Time", value: "duration" },
    { label: "Drone Type", value: "model" },
    { label: "Average Altitude", value: "avg_altitude" },
    { label: "Max Altitude", value: "max_altitude" },
    { label: "Average Speed", value: "avg_speed" },
    { label: "Max Speed", value: "max_speed" },
]

const SORT_ORDER_OPTIONS: { label: string; value: ReportSort["order"] }[] = [
    { label: "Ascending", value: "asc" },
    { label: "Descending", value: "desc" },
]

const OPTIONS = {
    REPORT_TYPE: [
        { label: "Flight Report", value: "flight_report" },
        { label: "COA Report", value: "coa_report" },
    ],
    DATE_RANGE_TYPE: [
        { label: "Relative", value: "relative" },
        { label: "Month", value: "month" },
        { label: "Year to Date", value: "year_to_date" },
        { label: "Custom", value: "custom" },
    ],
    FREQUENCY: [
        { label: "Once", value: "once" },
        { label: "Weekly", value: "weekly" },
        { label: "Monthly", value: "monthly" },
        { label: "Annually", value: "annually" },
    ],
    MIN_FLIGHT_TIME: [
        { label: "None", value: null },
        { label: "1 minute", value: 60 },
        { label: "2 minutes", value: 120 },
        { label: "3 minutes", value: 180 },
    ],
    MIN_DISTANCE: [
        { label: "None", value: null },
        { label: "10 feet", value: 10 },
        { label: "25 feet", value: 25 },
        { label: "50 feet", value: 50 },
    ],
} as const

const RELATIVE_PRESETS: SelectItem<number | "last_calendar_month", never>[] = [
    { label: "Last 24 hours", value: 1 },
    { label: "Last 7 days", value: 7 },
    { label: "Last 30 days", value: 30 },
    { label: "Last 90 days", value: 90 },
    { label: "Last year", value: 365 },
    { options: [{ label: "Last calendar month", value: "last_calendar_month" }] },
]

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
    label: new Date(2000, i).toLocaleString("en", { month: "long" }),
    value: i + 1,
}))

const currentYear = new Date().getFullYear()
const oldestMissionQuery = createQuery({
    resource: "metadata",
    aggregates: [{ field: "started_at", func: "MIN", alias: "oldestMissionDate" }],
})
const useYearToDateOptions = () => {
    const { data } = usePilotTrackingQuery(oldestMissionQuery)
    const oldestYear = data?.[0]?.oldestMissionDate?.getUTCFullYear() ?? currentYear
    const options = useMemo(
        () => [
            { label: "Current Year", value: "current" as const },
            ...Array.from({ length: currentYear - oldestYear }, (_, i) => ({
                label: `${currentYear - i - 1} to date`,
                value: currentYear - i - 1,
            })),
        ],
        [oldestYear]
    )
    return options
}

const yearsWithMissionsQuery = createQuery({
    resource: "metadata",
    timeBuckets: [{ field: "started_at", unit: "year", alias: "year" }],
    aggregates: [{ field: "mission_id", func: "COUNT", alias: "flightCount" }],
})
const useYearOptions = () => {
    const { data } = usePilotTrackingQuery(yearsWithMissionsQuery)
    const options = useMemo(() => {
        const validData =
            data
                ?.map((d) => d.year?.getUTCFullYear() ?? 0)
                .filter((d) => !!d)
                .toSorted()
                .toReversed() ?? []
        return validData.map((d) => ({
            label: `${d}`,
            value: d,
        }))
    }, [data])
    return options
}

const SEND_TIME_OPTIONS = [0, 6, 9, 12, 15, 18, 21].map((hour) => {
    const ampm = hour >= 12 ? "PM" : "AM"
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return {
        label: `${displayHour}:00 ${ampm}`,
        value: `${hour.toString().padStart(2, "0")}:00`,
    }
})

const buildDateRange = (form: ReportTemplateFormState): ReportDateRange => {
    switch (form.dateRangeType) {
        case "relative":
            if (form.relativeDays === "last_calendar_month") return { type: "last_calendar_month" }
            return { type: "relative", days: form.relativeDays }
        case "month":
            return { type: "month", year: form.monthYear, month: form.monthMonth }
        case "year_to_date":
            return { type: "year_to_date", year: form.yearToDateYear }
        case "custom":
            return { type: "custom", start: form.customStart, end: form.customEnd }
    }
}

const toFormState = (
    v: Partial<CreatableReportTemplate>,
    profileTimezoneIana: string | null
): ReportTemplateFormState => {
    const dr = v.date_range
    return {
        name: v.name ?? "",
        description: v.description ?? null,
        reportType: v.report_type ?? "flight_report",
        dateRangeType: dr?.type === "last_calendar_month" ? "relative" : (dr?.type ?? "relative"),
        relativeDays:
            dr?.type === "last_calendar_month"
                ? "last_calendar_month"
                : dr?.type === "relative"
                  ? dr.days
                  : 1,
        monthYear: dr?.type === "month" ? dr.year : "current",
        monthMonth: dr?.type === "month" ? dr.month : "current",
        yearToDateYear: dr?.type === "year_to_date" ? dr.year : "current",
        customStart: dr?.type === "custom" ? dr.start : "",
        customEnd: dr?.type === "custom" ? dr.end : "",
        isActive: v.notification_config?.active ?? false,
        recipients: v.notification_config?.email.recipients.addresses ?? [],
        frequency: v.schedule?.frequency ?? "monthly",
        sendTime: v.schedule?.send_time ?? "09:00",
        startDate: v.schedule?.start_date ?? new Date().toISOString().split("T")[0],
        timezone: v.schedule?.timezone ?? profileTimezoneIana,
        saveTimezoneAsDefault: false,
    }
}

export interface ReportTemplateFormProps {
    initialValues?: Partial<CreatableReportTemplate>
    operation: "create" | "edit" | "generate"
    onSubmit: (data: CreatableReportTemplate, downloadOnComplete: boolean) => Promise<void>
    afterSubmit?: () => void
    onCancel: () => void
}

export const ReportTemplateForm = ({
    initialValues,
    operation,
    onSubmit,
    afterSubmit,
    onCancel,
}: ReportTemplateFormProps) => {
    const dispatch = useAppDispatch()
    const profileTimezoneId = useAppSelector(selectTimezoneId)
    const profileTimezoneIana =
        possibleTimezones.find((t) => t.id === profileTimezoneId)?.iana ?? null

    const [form, setForm] = useState<ReportTemplateFormState>(
        toFormState(initialValues ?? {}, profileTimezoneIana)
    )

    const [filters, setFilters] = useState<ReportFilters>(initialValues?.filters ?? {})
    const [format, setFormat] = useState<ReportColumns>(
        initialValues?.format_config?.columns ?? DEFAULT_COLUMNS[form.reportType]
    )
    const [units, setUnits] = useState<ReportUnits>(initialValues?.format_config?.units ?? {})
    const [sort, setSort] = useState<ReportSort | undefined>(initialValues?.format_config?.sort)
    const [isDownloadChecked, setIsDownloadChecked] = useState(false)
    const [hasChanges, setHasChanges] = useState(operation !== "edit")
    const didSubmit = useRef(false)

    const setField: FieldSetter = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }))
        didSubmit.current = false
        setHasChanges(true)
    }

    useEffect(() => {
        if (operation !== "edit") setFormat(DEFAULT_COLUMNS[form.reportType])
    }, [form.reportType, operation])
    const setFilter: FilterSetter = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }))
        didSubmit.current = false
        setHasChanges(true)
    }

    const handleSetFormat = useCallback((v: ReportColumns) => {
        setFormat(v)
        didSubmit.current = false
        setHasChanges(true)
    }, [])
    const handleSetUnits = useCallback((v: ReportUnits) => {
        setUnits(v)
        didSubmit.current = false
        setHasChanges(true)
    }, [])
    const handleSetSort = useCallback((v: ReportSort | undefined) => {
        setSort(v)
        didSubmit.current = false
        setHasChanges(true)
    }, [])

    const payload: CreatableReportTemplate = {
        name: form.name,
        description: form.description,
        report_type: form.reportType,
        date_range: buildDateRange(form),
        filters,
        format_config: { columns: format, units, sort },
        schedule: form.isActive
            ? {
                  frequency: form.frequency,
                  send_time: form.sendTime,
                  start_date: form.startDate,
                  timezone: form.timezone ?? undefined,
              }
            : null,
        notification_config: form.isActive
            ? {
                  active: form.isActive,
                  email: {
                      recipients: {
                          addresses: form.recipients,
                      },
                  },
              }
            : null,
    }

    const isValid =
        creatableReportTemplateSchema.safeParse(payload).success &&
        (!form.isActive || form.timezone !== null)

    const confirmationModal = useConfirmationModalContext()
    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            !didSubmit.current && hasChanges && currentLocation.pathname !== nextLocation.pathname
    )
    useEffect(() => {
        if (blocker.state === "blocked" && !confirmationModal.isOpen) {
            confirmationModal
                .confirm({
                    RenderComponent: getGenericConfirmationModalComponent({
                        title: "Discard Changes?",
                        message: "Your unsaved changes will be discarded, do you want to proceed?",
                        confirmText: "Discard Changes",
                    }),
                })
                .then((res) => {
                    if (res) {
                        blocker.proceed()
                    } else {
                        blocker.reset()
                    }
                })
        }
    }, [blocker, blocker.state, confirmationModal])

    const [submitting, setSubmitting] = useState(false)
    const [isHoveringSubmit, setIsHoveringSubmit] = useState(false)
    const [submitMousePos, setSubmitMousePos] = useState({ x: 0, y: 0 })
    const handleSubmit = async () => {
        setSubmitting(true)
        try {
            if (form.saveTimezoneAsDefault && form.timezone) {
                const timezoneId = possibleTimezones.find((t) => t.iana === form.timezone)?.id
                if (timezoneId) {
                    await fetchApiGateway("patch /me/profile", { body: { timezoneId } })
                    dispatch(setTimezoneId(timezoneId))
                }
            }
            await onSubmit(payload, isDownloadChecked)
            didSubmit.current = true
            setHasChanges(false)
            afterSubmit?.()
        } catch (err) {
            console.error(err)
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Card className="flex flex-col max-w-[700px] w-full max-h-fit !p-0">
            <ScrollableArea className="pb-5">
                <MainSection form={form} setField={setField} operation={operation} />
                <Divider className="bg-white-100" />
                <FiltersSection filters={filters} setFilter={setFilter} />
                <FormatSection
                    format={format}
                    setFormat={handleSetFormat}
                    units={units}
                    setUnits={handleSetUnits}
                    sort={sort}
                    setSort={handleSetSort}
                />
            </ScrollableArea>
            <Divider className="bg-white-100" />
            <div className="flex justify-end items-center gap-2 p-5">
                {operation === "create" && (
                    <div className="flex mr-auto items-center gap-3">
                        <TriCheckbox
                            onClick={() => setIsDownloadChecked((prev) => !prev)}
                            state={isDownloadChecked ? "checked" : "unchecked"}
                            isDisabled={submitting}
                        />
                        <h5 className="text-gray-100">Download when created</h5>
                    </div>
                )}
                {hasChanges && <small className="text-gray-300">* Unsaved changes</small>}
                <ButtonSecondary title="Cancel" onClick={onCancel} disabled={submitting} />
                <div
                    className="relative"
                    onMouseEnter={() => setIsHoveringSubmit(true)}
                    onMouseLeave={() => setIsHoveringSubmit(false)}
                    onMouseMove={(e) => setSubmitMousePos({ x: e.clientX, y: e.clientY })}
                >
                    <ButtonPrimary
                        title={
                            operation === "create"
                                ? "Create Template"
                                : operation === "edit"
                                  ? "Save Changes"
                                  : "Generate Report"
                        }
                        onClick={handleSubmit}
                        disabled={!isValid || submitting}
                    />
                    {isHoveringSubmit && form.isActive && form.timezone === null && (
                        <PortalledTooltip initialPosition={submitMousePos}>
                            <div className="bg-gray-900 rounded-lg border border-gray-700 px-2 py-1 shadow-tooltip">
                                <small className="text-gray-50">
                                    A time zone is required to schedule email reports
                                </small>
                            </div>
                        </PortalledTooltip>
                    )}
                </div>
            </div>
        </Card>
    )
}

const MainSection = ({
    form,
    setField,
    operation,
}: {
    form: ReportTemplateFormState
    setField: FieldSetter
    operation: "create" | "edit" | "generate"
}) => {
    const [editing, setEditing] = useState<"title" | "description" | "none">("none")
    const yearToDateOptions = useYearToDateOptions()
    const yearOptions = useYearOptions()
    const profileTimezoneId = useAppSelector(selectTimezoneId)
    return (
        <div className="flex flex-col gap-5 py-6 px-20">
            <div className="flex flex-col gap-1">
                <ClickAwayListenerV2
                    onClickAway={() => {
                        if (editing === "title") setEditing("none")
                    }}
                >
                    <div
                        className={classNames("w-full", {
                            "cursor-text": editing !== "title",
                        })}
                        onClick={() => {
                            if (editing === "title") return
                            setEditing("title")
                        }}
                    >
                        <RenamableText
                            Tag="h2"
                            id="checklist-name"
                            title={form.name}
                            initialTitleIsPlaceholder={false} // todo
                            isRenaming={editing === "title"}
                            className="[&_h2]:text-[28px] [&_h2]:text-white"
                            onConfirmRenaming={(_, v) => {
                                setField("name", v)
                                setEditing("none")
                            }}
                            onCancelRenaming={() => {
                                setEditing("none")
                            }}
                        />
                    </div>
                </ClickAwayListenerV2>
                <ClickAwayListenerV2
                    onClickAway={() => {
                        if (editing === "description") setEditing("none")
                    }}
                >
                    <div
                        className={classNames("w-full", {
                            "cursor-text": editing !== "title",
                        })}
                        onClick={() => {
                            if (editing === "description") return
                            setEditing("description")
                        }}
                    >
                        <RenamableText
                            Tag="h5"
                            id="checklist-description"
                            title={form.description ?? "Description"}
                            initialTitleIsPlaceholder={!form.description}
                            isRenaming={editing === "description"}
                            className={classNames({
                                "[&_h5]:text-gray-300": editing !== "description",
                                "[&_h5]:text-white": editing === "description",
                            })}
                            onConfirmRenaming={(_, v) => {
                                setField("description", v)
                                setEditing("none")
                            }}
                            onCancelRenaming={() => {
                                setEditing("none")
                            }}
                        />
                    </div>
                </ClickAwayListenerV2>
            </div>

            <div className="flex flex-col gap-2">
                <h4 className="text-gray-300">Report Type</h4>
                <Select
                    options={OPTIONS.REPORT_TYPE}
                    value={form.reportType}
                    onChange={(v) => setField("reportType", v)}
                />
            </div>

            <div className="flex gap-4">
                <div
                    className={classNames("flex flex-col gap-2", {
                        "flex-1": form.dateRangeType === "custom" || form.dateRangeType === "month",
                        "flex-[0.25]":
                            form.dateRangeType === "relative" ||
                            form.dateRangeType === "year_to_date",
                    })}
                >
                    <h4 className="text-gray-300">Date Range Type</h4>
                    <Select
                        options={OPTIONS.DATE_RANGE_TYPE}
                        value={form.dateRangeType}
                        onChange={(v) => setField("dateRangeType", v)}
                    />
                </div>
                {form.dateRangeType === "relative" && (
                    <div className="flex flex-col gap-2 flex-[0.75]">
                        <h4 className="text-gray-300">Date Preset</h4>
                        <Select
                            options={RELATIVE_PRESETS}
                            value={form.relativeDays}
                            onChange={(v) => setField("relativeDays", v)}
                            className="!min-w-0"
                        />
                    </div>
                )}
                {form.dateRangeType === "month" && (
                    <>
                        <div className="flex flex-col gap-2 flex-1">
                            <h4 className="text-gray-300">Month</h4>
                            <Select
                                options={MONTH_OPTIONS}
                                value={form.monthMonth}
                                onChange={(v) => setField("monthMonth", v)}
                                className="!min-w-0"
                            />
                        </div>
                        <div className="flex flex-col gap-2 flex-1">
                            <h4 className="text-gray-300">Year</h4>
                            <Select
                                options={yearOptions}
                                value={form.monthYear}
                                onChange={(v) => setField("monthYear", v)}
                                className="!min-w-0"
                            />
                        </div>
                    </>
                )}
                {form.dateRangeType === "year_to_date" && (
                    <div className="flex flex-col gap-2 flex-1">
                        <h4 className="text-gray-300">Year</h4>
                        <Select
                            options={yearToDateOptions}
                            value={form.yearToDateYear}
                            onChange={(v) => setField("yearToDateYear", v)}
                        />
                    </div>
                )}
                {form.dateRangeType === "custom" && (
                    <>
                        <div className="flex flex-col gap-2 flex-1">
                            <h4 className="text-gray-300">Start</h4>
                            <Input
                                type="date"
                                value={form.customStart}
                                onChange={(e) => setField("customStart", e.currentTarget.value)}
                            />
                        </div>
                        <div className="flex flex-col gap-2 flex-1">
                            <h4 className="text-gray-300">End</h4>
                            <Input
                                type="date"
                                value={form.customEnd}
                                onChange={(e) => setField("customEnd", e.currentTarget.value)}
                            />
                        </div>
                    </>
                )}
            </div>

            {operation !== "generate" && (
                <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                        <h4 className="text-white">Email Recipients</h4>
                        <ToggleSwitch
                            isToggled={form.isActive}
                            setIsToggled={(v) => setField("isActive", v)}
                        />
                    </div>
                    <ExpandableContent isOpen={form.isActive}>
                        <div className="flex flex-col gap-3">
                            <div className="flex flex-col gap-2">
                                <small className="text-gray-300">Recipients</small>
                                <ContactInputMultiSelect
                                    enabledFeatures={["email"]}
                                    emails={form.recipients}
                                    setEmails={(v) => setField("recipients", v)}
                                    placeholder="Add names, emails, or phone numbers"
                                />
                            </div>
                            <div className="flex gap-4">
                                <div className="flex flex-col gap-2 flex-1">
                                    <small className="text-gray-300">Start Date</small>
                                    <Input
                                        type="date"
                                        value={form.startDate}
                                        onChange={(e) =>
                                            setField("startDate", e.currentTarget.value)
                                        }
                                    />
                                </div>
                                <div className="flex flex-col gap-2 flex-1">
                                    <small className="text-gray-300">Frequency</small>
                                    <Select
                                        options={OPTIONS.FREQUENCY}
                                        value={form.frequency}
                                        onChange={(v) => setField("frequency", v)}
                                        className="!min-w-0"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div className="flex flex-col gap-2 flex-[3]">
                                    <small className="text-gray-300">Time Zone*</small>
                                    <Select
                                        options={possibleTimezones.map((t) => ({
                                            label: t.name,
                                            value: t.iana,
                                        }))}
                                        value={form.timezone ?? undefined}
                                        onChange={(v) => setField("timezone", v ?? null)}
                                        placeholder="Not Selected"
                                        className="!w-full"
                                    />
                                </div>
                                <div className="flex flex-col gap-2 flex-[2]">
                                    <small className="text-gray-300">Time</small>
                                    <Select
                                        options={SEND_TIME_OPTIONS}
                                        value={form.sendTime}
                                        onChange={(v) => setField("sendTime", v)}
                                        className="!min-w-0"
                                    />
                                </div>
                            </div>
                            {profileTimezoneId === null && (
                                <div className="flex items-center gap-3">
                                    <TriCheckbox
                                        onClick={() =>
                                            setField(
                                                "saveTimezoneAsDefault",
                                                !form.saveTimezoneAsDefault
                                            )
                                        }
                                        state={form.saveTimezoneAsDefault ? "checked" : "unchecked"}
                                        size="small"
                                    />
                                    <small className="text-gray-300">
                                        Save as my default time zone
                                    </small>
                                </div>
                            )}
                        </div>
                    </ExpandableContent>
                </div>
            )}
        </div>
    )
}

const MultiCheckboxSelect = ({
    options,
    selectedIds,
    onChange,
    allLabel,
}: {
    options: { label: string; value: string }[]
    selectedIds: string[] | undefined
    onChange: (ids: string[] | undefined) => void
    allLabel: string
}) => {
    const [search, setSearch] = useState("")

    const filtered = useMemo(
        () => options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase())),
        [options, search]
    )

    const isEffectivelyAll = useMemo(
        () => selectedIds === undefined || options.every((o) => selectedIds.includes(o.value)),
        [options, selectedIds]
    )

    const toggle = useCallback(
        (id: string) => {
            if (isEffectivelyAll) {
                onChange(options.filter((o) => o.value !== id).map((o) => o.value))
            } else if (selectedIds?.includes(id)) {
                onChange(selectedIds.filter((v) => v !== id))
            } else {
                const next = [...(selectedIds ?? []), id]
                onChange(next.length === options.length ? undefined : next)
            }
        },
        [isEffectivelyAll, onChange, options, selectedIds]
    )

    const triggerLabel = useMemo(
        () => (isEffectivelyAll ? allLabel : `${selectedIds?.length ?? 0} selected`),
        [allLabel, isEffectivelyAll, selectedIds?.length]
    )

    return (
        <Dropdown>
            <div className="block w-full">
                <Dropdown.Trigger
                    as="button"
                    aria-haspopup="listbox"
                    className="group justify-between border border-transparent pl-4 py-2 pr-2 text-left w-full rounded-lg bg-gray-700 aria-expanded:border-blue-200"
                >
                    <span className="text-gray-0">{triggerLabel}</span>
                    <Icons.ArrowDropUp className="text-gray-300 hidden group-aria-expanded:block" />
                    <Icons.ArrowDropdown className="text-gray-300 group-aria-expanded:hidden" />
                </Dropdown.Trigger>
            </div>
            <Dropdown.Menu
                unstyled
                matchTriggerWidth
                className="my-1 shadow-lg bg-gray-700 rounded-[6px]"
            >
                <div className="flex items-center gap-3 px-4 pt-3 pb-2 text-white">
                    <InputOutlined
                        placeholder="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="flex-[0.85]"
                    />
                    <IconButton
                        onClick={() => onChange(isEffectivelyAll ? [] : undefined)}
                        className="shrink-0 min-w-max flex-[0.15]"
                    >
                        <small className="flex items-center justify-center">
                            {isEffectivelyAll ? "Deselect All" : "Select All"}
                        </small>
                    </IconButton>
                </div>
                <ScrollableArea className="max-h-48 py-1">
                    <div className="flex flex-col">
                        {filtered.map((opt) => (
                            <Dropdown.Item
                                key={opt.value}
                                clickBehaviour="keep-open"
                                onClick={() => toggle(opt.value)}
                                className="flex items-center gap-3 px-4 py-2 hover:bg-gray-500 cursor-pointer"
                            >
                                <TriCheckbox
                                    state={
                                        isEffectivelyAll || selectedIds?.includes(opt.value)
                                            ? "checked"
                                            : "unchecked"
                                    }
                                    onClick={() => toggle(opt.value)}
                                />
                                <span className="text-white text-sm">{opt.label}</span>
                            </Dropdown.Item>
                        ))}
                    </div>
                </ScrollableArea>
            </Dropdown.Menu>
        </Dropdown>
    )
}

const FiltersSection = ({
    filters,
    setFilter,
}: {
    filters: ReportFilters
    setFilter: FilterSetter
}) => {
    const { pilots } = usePilots()
    const { aircraft } = useAircraft()

    const pilotOptions = useMemo(
        () =>
            (pilots ?? []).map((u) => ({
                label: u.fullName ?? u.email ?? u.id ?? "",
                value: u.id ?? "",
            })),
        [pilots]
    )

    const aircraftOptions = useMemo(
        () =>
            aircraft?.map((u) => ({
                label: u.nickname ?? u.manufacturer + u.model,
                value: u.id ?? "",
            })),
        [aircraft]
    )

    return (
        <AccordionSection title="Filters">
            <div className="flex flex-col gap-4">
                <h4 className="text-gray-300">Criteria</h4>
                <div className="flex flex-col gap-2">
                    <h5 className="text-gray-300">People</h5>
                    <MultiCheckboxSelect
                        options={pilotOptions}
                        selectedIds={filters.pilot_ids}
                        onChange={(ids) => setFilter("pilot_ids", ids)}
                        allLabel="All People"
                    />
                </div>

                <div className="flex flex-col gap-2">
                    <h5 className="text-gray-300">Drones</h5>
                    <MultiCheckboxSelect
                        options={aircraftOptions}
                        selectedIds={filters.aircraft_ids}
                        onChange={(ids) => setFilter("aircraft_ids", ids)}
                        allLabel="All Drones"
                    />
                </div>
            </div>
            <div className="w-full h-px bg-gray-700 my-5" />
            <div className="flex flex-col gap-4">
                <h4 className="text-gray-300">Requirements</h4>
                <div className="flex items-center justify-between gap-4">
                    <h5 className="text-gray-300">Minimum flight time</h5>
                    <Select
                        options={OPTIONS.MIN_FLIGHT_TIME}
                        value={filters.min_flight_time ?? null}
                        onChange={(v) => setFilter("min_flight_time", v ?? undefined)}
                    />
                </div>
                <div className="flex items-center justify-between gap-4">
                    <h5 className="text-gray-300">Minimum distance</h5>
                    <Select
                        options={OPTIONS.MIN_DISTANCE}
                        value={filters.min_distance ?? null}
                        onChange={(v) => setFilter("min_distance", v ?? undefined)}
                    />
                </div>
            </div>
        </AccordionSection>
    )
}

const FormatSection = ({
    format,
    setFormat,
    units,
    setUnits,
    sort,
    setSort,
}: {
    format: ReportColumns
    setFormat: (v: ReportColumns) => void
    units: ReportUnits
    setUnits: (v: ReportUnits) => void
    sort: ReportSort | undefined
    setSort: (v: ReportSort | undefined) => void
}) => {
    const isGroupSelected = (group: ReportColumnGroup) => {
        const current = selectedKeys(format, group)
        return groupKeys(group).every((key) => current.includes(key))
    }

    const toggleColumn = (group: ReportColumnGroup, key: string) => {
        const current = selectedKeys(format, group)
        const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key]

        setFormat(withSelectedKeys(format, group, next))
    }

    const toggleGroup = (group: ReportColumnGroup) =>
        setFormat(withSelectedKeys(format, group, isGroupSelected(group) ? [] : groupKeys(group)))

    const allGroupsSelected = REPORT_COLUMN_GROUPS.every(isGroupSelected)

    const toggleAll = () =>
        setFormat(
            REPORT_COLUMN_GROUPS.reduce(
                (next, group) =>
                    withSelectedKeys(next, group, allGroupsSelected ? [] : groupKeys(group)),
                format
            )
        )

    return (
        <AccordionSection title="Format" className="text-gray-300">
            <div className="flex flex-col gap-5">
                <div className="flex justify-between">
                    <small>Column labels</small>
                    <IconButton onClick={toggleAll}>
                        <small>
                            {allGroupsSelected ? "Deselect All Labels" : "Select All Labels"}
                        </small>
                    </IconButton>
                </div>

                <div className="flex flex-col gap-5">
                    {REPORT_COLUMN_GROUPS.map((group) => {
                        const columns = REPORT_COLUMNS[group]
                        const selected = selectedKeys(format, group)
                        const allSelected = isGroupSelected(group)
                        return (
                            <NestedAccordionSection
                                key={group}
                                title={`${group[0].toUpperCase()}${group.slice(1)}`}
                                rightItemWhenOpen={
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            toggleGroup(group)
                                        }}
                                    >
                                        <small>{allSelected ? "Deselect All" : "Select All"}</small>
                                    </IconButton>
                                }
                            >
                                <div className="grid grid-cols-2 gap-x-5 gap-y-3">
                                    {columns.map((col) => (
                                        <div key={col.key} className="flex items-center gap-2">
                                            <TriCheckbox
                                                state={
                                                    selected.includes(col.key)
                                                        ? "checked"
                                                        : "unchecked"
                                                }
                                                onClick={() => toggleColumn(group, col.key)}
                                            />
                                            <h5 className="text-gray-100">{col.label}</h5>
                                        </div>
                                    ))}
                                </div>
                            </NestedAccordionSection>
                        )
                    })}
                </div>

                <div className="flex flex-col gap-4 mt-6">
                    <h4 className="text-gray-300">Unit Preferences</h4>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col gap-2">
                            <h5 className="text-gray-300">Date</h5>
                            <Select
                                options={UNITS_OPTIONS.DATE}
                                value={units.date ?? "MM/DD/YYYY"}
                                onChange={(v) => setUnits({ ...units, date: v })}
                            />
                        </div>
                        <div className="flex flex-col gap-2">
                            <h5 className="text-gray-300">Time</h5>
                            <Select
                                options={UNITS_OPTIONS.TIME}
                                value={units.time ?? "12h"}
                                onChange={(v) => setUnits({ ...units, time: v })}
                            />
                        </div>
                        <div className="flex flex-col gap-2">
                            <h5 className="text-gray-300">Distance</h5>
                            <Select
                                options={UNITS_OPTIONS.DISTANCE}
                                value={units.distance ?? "feet"}
                                onChange={(v) => setUnits({ ...units, distance: v })}
                            />
                        </div>
                        <div className="flex flex-col gap-2">
                            <h5 className="text-gray-300">Altitude</h5>
                            <Select
                                options={UNITS_OPTIONS.ALTITUDE}
                                value={units.altitude ?? "feet"}
                                onChange={(v) => setUnits({ ...units, altitude: v })}
                            />
                        </div>
                        <div className="flex flex-col gap-2">
                            <h5 className="text-gray-300">Speed</h5>
                            <Select
                                options={UNITS_OPTIONS.SPEED}
                                value={units.speed ?? "mph"}
                                onChange={(v) => setUnits({ ...units, speed: v })}
                            />
                        </div>
                        <div className="flex flex-col gap-2">
                            <h5 className="text-gray-300">Temperature</h5>
                            <Select
                                options={UNITS_OPTIONS.TEMPERATURE}
                                value={units.temperature ?? "fahrenheit"}
                                onChange={(v) => setUnits({ ...units, temperature: v })}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex flex-col gap-4 mt-6">
                    <h4 className="text-gray-300">Sort By</h4>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col gap-2">
                            <h5 className="text-gray-300">Type</h5>
                            <Select
                                options={SORT_FIELD_OPTIONS}
                                value={sort?.field ?? "started_at"}
                                onChange={(v) => setSort({ field: v, order: sort?.order ?? "asc" })}
                            />
                        </div>
                        <div className="flex flex-col gap-2">
                            <h5 className="text-gray-300">Order</h5>
                            <Select
                                options={SORT_ORDER_OPTIONS}
                                value={sort?.order ?? "asc"}
                                onChange={(v) =>
                                    setSort({ field: sort?.field ?? "started_at", order: v })
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>
        </AccordionSection>
    )
}
interface AccordionSectionProps {
    title: string
    children: React.ReactNode
    className?: string
}

const AccordionSection = ({ title, children, className }: AccordionSectionProps) => {
    const [isOpen, setIsOpen] = useState(false)

    return (
        <div className={classNames("flex flex-col px-20", className)}>
            <button
                type="button"
                className="flex items-center gap-2 py-4 w-full text-left"
                onClick={() => setIsOpen((prev) => !prev)}
            >
                <Icons.Icon
                    Icon={isOpen ? Icons.ArrowDropdown : Icons.ArrowDropdownRight}
                    className="text-gray-300"
                />
                <h2 className="text-gray-0">{title}</h2>
            </button>
            <ExpandableContent isOpen={isOpen}>
                <div className="pl-8">{children}</div>
            </ExpandableContent>
        </div>
    )
}

const NestedAccordionSection = ({
    title,
    children,
    className,
    rightItemWhenOpen,
}: AccordionSectionProps & { rightItemWhenOpen?: JSX.Element }) => {
    const [isOpen, setIsOpen] = useState(true)

    return (
        <div className={classNames("flex flex-col", className)}>
            <button
                type="button"
                className="flex items-center gap-2 py-1.5 w-full text-left"
                onClick={() => setIsOpen((prev) => !prev)}
            >
                <Icons.Icon
                    Icon={isOpen ? Icons.ArrowDropdown : Icons.ArrowDropdownRight}
                    className="text-gray-300"
                />
                <h4 className="text-gray-300">{title}</h4>
                <div className="grow" />
                {isOpen && rightItemWhenOpen}
            </button>
            <ExpandableContent isOpen={isOpen}>
                <div className="pt-2 pl-2">{children}</div>
            </ExpandableContent>
        </div>
    )
}
